/**
 * Pure helpers + wire types for the import assistant chat panel
 * (components/import/ChatPanel.tsx). Wire types are hand-mirrored from
 * apps/api/src/routes/client/import.ts (GET/POST /batches/:batchId/chat) and
 * services/import/agent-db.ts; keep them in sync.
 *
 * The assistant's text is MODEL output and may echo a customer file, so it is
 * never rendered as HTML: parseChatText() turns it into plain text segments
 * (paragraphs, bullets, **bold**, `code`) and strips every link and image form.
 * A markdown image or link is an exfiltration channel. The only link in the
 * panel is generated by the UI, never parsed from model text.
 */
import type { Proposal } from './importReview';

export interface ChatMessage {
  id: string;
  seq: number;
  role: 'user' | 'assistant';
  text: string;
  proposalIds: string[];
  createdAt: string;
}

export interface ChatPayload {
  messages: ChatMessage[];
  proposals: Proposal[];
  escalatedAt: string | null;
  assistantEnabled: boolean;
  turnsUsed: number;
  turnsMax: number;
}

export type TurnStatus = 'ok' | 'timeout' | 'disabled' | 'capped' | 'locked' | 'budget' | 'unavailable';

export interface TurnReply {
  status: TurnStatus;
  reply: { text: string; proposalIds: string[] } | null;
  proposals: Proposal[];
}

/** Sent by the "Talk to a person" button; the server decides whether to escalate, not the button. */
export const TALK_TO_PERSON_MESSAGE = 'I would like to talk to a person on the SiliconX team.';

export function chatDisabledReason(i: {
  isAdmin: boolean;
  locked: boolean;
  assistantEnabled: boolean;
  turnsUsed: number;
  turnsMax: number;
}): string | null {
  if (!i.isAdmin) return 'Only organization admins can chat.';
  if (i.locked) return 'This import has been submitted and can no longer be changed.';
  if (!i.assistantEnabled) return 'The assistant is not available. Use the suggested fixes, or contact support.';
  if (i.turnsUsed >= i.turnsMax) return `Chat limit reached (${i.turnsMax} messages). Use the suggested fixes, or contact support.`;
  return null;
}

/** Customer-facing explanation of a non-ok turn status; null = nothing to add (the reply text says it). */
export function turnStatusNote(status: TurnStatus): string | null {
  switch (status) {
    case 'ok':
      return null;
    case 'timeout':
      return null;
    case 'disabled':
      return 'The assistant is not available. Use the suggested fixes, or contact support.';
    case 'capped':
      return 'Chat limit reached for this import. Use the suggested fixes, or contact support.';
    case 'locked':
      return 'This import has been submitted and can no longer be changed.';
    case 'budget':
    case 'unavailable':
      return null;
  }
}

// ---------------------------------------------------------------------------
// Safe rendering
// ---------------------------------------------------------------------------

export type InlineSeg = { kind: 'text' | 'bold' | 'code'; text: string };
export type TextBlock = { kind: 'p'; segs: InlineSeg[] } | { kind: 'ul'; items: InlineSeg[][] };

const LINK_REMOVED = '[link removed]';
const SCHEMES = /\b(?:https?|ftp|file|data|javascript|mailto|vbscript):[^\s)>\]]*/gi;
const WWW = /\bwww\.[^\s)>\]]+/gi;

/** Remove every link / image / HTML form from model text. Defense in depth: the server does the same before storing. */
export function stripLinks(text: string): string {
  return text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, LINK_REMOVED)
    .replace(/^\s*\[[^\]]+\]:\s*\S+.*$/gm, LINK_REMOVED)
    .replace(/\[([^\]]*)\]\([^)]*\)/g, (_m, label: string) => `${label} ${LINK_REMOVED}`)
    .replace(/<\/?[a-z][^>]*>/gi, '')
    .replace(SCHEMES, LINK_REMOVED)
    .replace(WWW, LINK_REMOVED)
    .replace(/<[^>\s]*>/g, LINK_REMOVED);
}

function inline(line: string): InlineSeg[] {
  const segs: InlineSeg[] = [];
  const re = /\*\*([^*]+)\*\*|`([^`]+)`/g;
  let last = 0;
  for (let m = re.exec(line); m; m = re.exec(line)) {
    if (m.index > last) segs.push({ kind: 'text', text: line.slice(last, m.index) });
    if (m[1] !== undefined) segs.push({ kind: 'bold', text: m[1] });
    else segs.push({ kind: 'code', text: m[2] ?? '' });
    last = m.index + m[0].length;
  }
  if (last < line.length) segs.push({ kind: 'text', text: line.slice(last) });
  return segs.length > 0 ? segs : [{ kind: 'text', text: '' }];
}

/** Plain text -> paragraphs, bullet lists, bold and code spans. Never produces HTML or a link. */
export function parseChatText(raw: string): TextBlock[] {
  const lines = stripLinks(raw).replace(/\r\n?/g, '\n').split('\n');
  const blocks: TextBlock[] = [];
  let para: string[] = [];
  let list: InlineSeg[][] | null = null;
  const flushPara = () => {
    if (para.length > 0) blocks.push({ kind: 'p', segs: inline(para.join(' ')) });
    para = [];
  };
  const flushList = () => {
    if (list) blocks.push({ kind: 'ul', items: list });
    list = null;
  };
  for (const line of lines) {
    const bullet = /^\s*(?:[-*]|\d+\.)\s+(.*)$/.exec(line);
    if (bullet) {
      flushPara();
      (list ??= []).push(inline(bullet[1] ?? ''));
    } else if (line.trim() === '') {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return blocks;
}
